import { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_BASE
    ? `${process.env.REACT_APP_API_BASE}/phpmailer/oznam.php`
    : '/phpmailer/oznam.php';

const TOKEN_KEY = 'petromin_admin_token';

const LOCATIONS = [
    { value: 'stavebniny',       label: 'Stavebniny' },
    { value: 'cerpacia_stanica', label: 'Čerpacia stanica' },
    { value: 'obe',              label: 'Obe stránky' },
];

const locationLabel = (value) =>
    (LOCATIONS.find((l) => l.value === value) || {}).label || value;

const inputClass =
    'w-full border-b-2 border-gray-300 outline-none p-3 bg-transparent focus:border-red-600 transition-colors duration-200';

// --- API helper -------------------------------------------------------------
async function callApi(action, payload = {}) {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...payload }),
    });
    const data = await res.json();
    return { ok: res.ok, data };
}

// --- Date helpers -----------------------------------------------------------
const formatDateTime = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const remainingLabel = (iso, visibleDays) => {
    const end = new Date(iso).getTime() + visibleDays * 24 * 3600 * 1000;
    const ms  = end - Date.now();
    if (ms <= 0) return null;
    const hours = Math.floor(ms / 3600000);
    const days  = Math.floor(hours / 24);
    return days >= 1 ? `zostáva ${days} d ${hours % 24} h` : `zostáva ${hours} h`;
};

const isExpired = (iso, visibleDays) =>
    Date.now() > new Date(iso).getTime() + visibleDays * 24 * 3600 * 1000;

// Mirrors the backend query: for each public page, the newest active
// non-expired oznam matching that page (or 'obe') is the one visitors see.
function computeLivePages(items, visibleDays) {
    const live = {};
    ['stavebniny', 'cerpacia_stanica'].forEach((page) => {
        const match = items.find(
            (it) =>
                it.active &&
                !isExpired(it.created_at, visibleDays) &&
                (it.location === page || it.location === 'obe')
        );
        if (match) live[match.id] = [...(live[match.id] || []), page];
    });
    return live;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
function LoginView({ onLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError]       = useState(null);
    const [loading, setLoading]   = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) return;

        setLoading(true);
        setError(null);
        try {
            const { data } = await callApi('login', { username: username.trim(), password });
            if (data.status === 'ok' && data.token) {
                onLogin(data.token);
            } else {
                setError(data.message || 'Prihlásenie zlyhalo.');
            }
        } catch {
            setError('Nepodarilo sa pripojiť k serveru.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-16">
            <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md">
                <div className="flex items-center gap-3 mb-8">
                    <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                        Admin
                    </span>
                    <span className="text-xl font-bold text-gray-800">Petromin</span>
                </div>

                <h1 className="text-3xl font-bold text-gray-800 mb-1">Prihlásenie</h1>
                <p className="text-gray-400 text-sm mb-10">Správa oznamov na stránke.</p>

                <form onSubmit={submit} className="space-y-6">
                    <input
                        type="text"
                        name="username"
                        autoComplete="username"
                        placeholder="Meno.."
                        className={inputClass}
                        value={username}
                        onChange={(e) => { setUsername(e.target.value); setError(null); }}
                        required
                    />
                    <input
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        placeholder="Heslo.."
                        className={inputClass}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(null); }}
                        required
                    />

                    {error && <p className="text-red-600 font-semibold text-sm">{error}</p>}

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-4 rounded font-semibold text-white transition-colors duration-200 ${
                            loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 cursor-pointer'
                        }`}
                    >
                        {loading ? 'Prihlasujem...' : 'Prihlásiť sa'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
function StatusBadge({ item, livePages, visibleDays }) {
    const base = 'text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider whitespace-nowrap';

    if (!item.active) {
        return <span className={`${base} bg-gray-200 text-gray-600`}>Skrytý</span>;
    }
    if (isExpired(item.created_at, visibleDays)) {
        return <span className={`${base} bg-gray-200 text-gray-600`}>Vypršaný</span>;
    }
    if (livePages && livePages.length) {
        return <span className={`${base} bg-green-600 text-white`}>Zobrazuje sa</span>;
    }
    return <span className={`${base} bg-amber-500 text-white`}>Prekrytý novším</span>;
}

// ---------------------------------------------------------------------------
// One row in the list
// ---------------------------------------------------------------------------
function OznamRow({ item, livePages, visibleDays, busy, onToggle, onDelete, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(item);

    useEffect(() => { setDraft(item); }, [item]);

    const startEdit = () => { setDraft(item); setEditing(true); };
    const cancel    = () => { setDraft(item); setEditing(false); };

    const save = async () => {
        const saved = await onSave(item.id, {
            title:    draft.title,
            message:  draft.message,
            location: draft.location,
        });
        if (saved) setEditing(false);
    };

    const remaining = item.active && !isExpired(item.created_at, visibleDays)
        ? remainingLabel(item.created_at, visibleDays)
        : null;

    if (editing) {
        return (
            <div className="border border-red-200 rounded-xl p-5 bg-red-50/40">
                <div className="space-y-4">
                    <input
                        type="text"
                        className={`${inputClass} bg-white rounded`}
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        placeholder="Titulok oznamu.."
                    />
                    <textarea
                        rows={4}
                        className={`${inputClass} bg-white rounded resize-none`}
                        value={draft.message}
                        onChange={(e) => setDraft({ ...draft, message: e.target.value })}
                        placeholder="Text oznamu.."
                    />
                    <select
                        className={`${inputClass} bg-white rounded`}
                        value={draft.location}
                        onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                    >
                        {LOCATIONS.map((l) => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                    </select>

                    <div className="flex gap-3 pt-1">
                        <button
                            onClick={save}
                            disabled={busy}
                            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold px-5 py-2 rounded transition-colors duration-200"
                        >
                            Uložiť
                        </button>
                        <button
                            onClick={cancel}
                            disabled={busy}
                            className="text-gray-500 hover:text-gray-800 font-semibold px-3 py-2 transition-colors duration-200"
                        >
                            Zrušiť
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors duration-200">
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge item={item} livePages={livePages} visibleDays={visibleDays} />
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
                        {locationLabel(item.location)}
                    </span>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDateTime(item.created_at)}
                    {remaining ? ` · ${remaining}` : ''}
                </span>
            </div>

            <h3 className="text-lg font-bold text-gray-800 mb-1">{item.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line mb-4">{item.message}</p>

            <div className="flex gap-4 text-sm font-semibold">
                <button
                    onClick={startEdit}
                    disabled={busy}
                    className="text-gray-600 hover:text-red-600 disabled:text-gray-300 transition-colors duration-200"
                >
                    Upraviť
                </button>
                <button
                    onClick={() => onToggle(item)}
                    disabled={busy}
                    className="text-gray-600 hover:text-red-600 disabled:text-gray-300 transition-colors duration-200"
                >
                    {item.active ? 'Skryť' : 'Zobraziť'}
                </button>
                <button
                    onClick={() => onDelete(item)}
                    disabled={busy}
                    className="text-gray-400 hover:text-red-600 disabled:text-gray-300 transition-colors duration-200 ml-auto"
                >
                    Zmazať
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------
function PanelView({ token, onLogout }) {
    const [items, setItems]           = useState([]);
    const [visibleDays, setVisibleDays] = useState(3);
    const [loading, setLoading]       = useState(true);
    const [busy, setBusy]             = useState(false);
    const [error, setError]           = useState(null);
    const [notice, setNotice]         = useState(null);

    const [form, setForm]       = useState({ title: '', message: '', location: 'stavebniny' });
    const [showForm, setShowForm] = useState(false);

    // Runs every API call, handling an expired session in one place.
    const request = useCallback(async (action, payload = {}) => {
        const { data } = await callApi(action, { token, ...payload });
        if (data.status === 'expired') {
            onLogout();
            return null;
        }
        return data;
    }, [token, onLogout]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await request('list');
            if (!data) return;
            if (data.status === 'ok') {
                setItems(data.items || []);
                if (data.visible_days) setVisibleDays(data.visible_days);
            } else {
                setError(data.message || 'Nepodarilo sa načítať oznamy.');
            }
        } catch {
            setError('Nepodarilo sa pripojiť k serveru.');
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    const flash = (msg) => {
        setNotice(msg);
        setTimeout(() => setNotice(null), 4000);
    };

    const runAction = async (action, payload, successMsg) => {
        setBusy(true);
        setError(null);
        try {
            const data = await request(action, payload);
            if (!data) return false;
            if (data.status === 'ok') {
                await load();
                flash(successMsg);
                return true;
            }
            setError(data.message || 'Akcia zlyhala.');
            return false;
        } catch {
            setError('Nepodarilo sa pripojiť k serveru.');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.message.trim()) return;

        const ok = await runAction(
            'create',
            { title: form.title.trim(), message: form.message.trim(), location: form.location },
            'Oznam bol pridaný a je viditeľný pre návštevníkov.'
        );
        if (ok) {
            setForm({ title: '', message: '', location: 'stavebniny' });
            setShowForm(false);
        }
    };

    const handleToggle = (item) =>
        runAction(
            'update',
            { id: item.id, active: !item.active },
            item.active ? 'Oznam bol skrytý.' : 'Oznam bol znova zobrazený.'
        );

    const handleDelete = (item) => {
        if (!window.confirm(`Naozaj zmazať oznam „${item.title}"? Túto akciu nie je možné vrátiť.`)) {
            return;
        }
        return runAction('delete', { id: item.id }, 'Oznam bol zmazaný.');
    };

    const handleSave = (id, fields) =>
        runAction('update', { id, ...fields }, 'Zmeny boli uložené.');

    const livePages   = computeLivePages(items, visibleDays);
    const liveCount   = Object.keys(livePages).length;
    const activeCount = items.filter((i) => i.active && !isExpired(i.created_at, visibleDays)).length;

    return (
        <div className="min-h-screen bg-gray-50 px-6 py-12">
            <div className="max-w-3xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                            Admin
                        </span>
                        <span className="text-xl font-bold text-gray-800">Petromin</span>
                    </div>
                    <button
                        onClick={onLogout}
                        className="text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors duration-200"
                    >
                        Odhlásiť sa
                    </button>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
                    <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-1">Oznamy</h1>
                            <p className="text-gray-400 text-sm">
                                Oznam sa zobrazuje {visibleDays} dni od pridania. Na každej stránke
                                sa návštevníkovi ukáže vždy len ten najnovší.
                            </p>
                        </div>
                        {!showForm && (
                            <button
                                onClick={() => setShowForm(true)}
                                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-3 rounded transition-colors duration-200 whitespace-nowrap"
                            >
                                + Nový oznam
                            </button>
                        )}
                    </div>

                    {/* Summary */}
                    <div className="flex gap-8 mb-8 pb-8 border-b border-gray-100">
                        <div>
                            <p className="text-3xl font-bold text-gray-800">{liveCount}</p>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Zobrazuje sa</p>
                        </div>
                        <div>
                            <p className="text-3xl font-bold text-gray-800">{activeCount}</p>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Aktívnych</p>
                        </div>
                        <div>
                            <p className="text-3xl font-bold text-gray-800">{items.length}</p>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Celkom</p>
                        </div>
                    </div>

                    {/* New announcement form */}
                    {showForm && (
                        <form onSubmit={handleCreate} className="space-y-6 mb-10 pb-10 border-b border-gray-100">
                            <h2 className="text-xl font-bold text-gray-800">Nový oznam</h2>
                            <input
                                type="text"
                                name="title"
                                placeholder="Titulok oznamu.."
                                className={inputClass}
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                required
                            />
                            <textarea
                                name="message"
                                rows={5}
                                placeholder="Text oznamu.."
                                className={`${inputClass} resize-none`}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                required
                            />
                            <select
                                name="location"
                                className={inputClass}
                                value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                            >
                                {LOCATIONS.map((l) => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </select>

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={busy}
                                    className={`px-6 py-3 rounded font-semibold text-white transition-colors duration-200 ${
                                        busy ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 cursor-pointer'
                                    }`}
                                >
                                    {busy ? 'Ukladám...' : 'Uložiť oznam'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    disabled={busy}
                                    className="text-gray-500 hover:text-gray-800 font-semibold px-3 py-3 transition-colors duration-200"
                                >
                                    Zrušiť
                                </button>
                            </div>
                        </form>
                    )}

                    {/* Messages */}
                    {notice && <p className="text-green-600 font-semibold text-sm mb-6">{notice}</p>}
                    {error  && <p className="text-red-600 font-semibold text-sm mb-6">{error}</p>}

                    {/* List */}
                    {loading ? (
                        <p className="text-gray-400 text-sm py-8 text-center">Načítavam oznamy...</p>
                    ) : items.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-400 mb-4">Zatiaľ tu nie sú žiadne oznamy.</p>
                            {!showForm && (
                                <button
                                    onClick={() => setShowForm(true)}
                                    className="text-red-600 hover:text-red-700 font-semibold transition-colors duration-200"
                                >
                                    Pridať prvý oznam
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items.map((item) => (
                                <OznamRow
                                    key={item.id}
                                    item={item}
                                    livePages={livePages[item.id]}
                                    visibleDays={visibleDays}
                                    busy={busy}
                                    onToggle={handleToggle}
                                    onDelete={handleDelete}
                                    onSave={handleSave}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function AdminOznam() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

    const login = (newToken) => {
        sessionStorage.setItem(TOKEN_KEY, newToken);
        setToken(newToken);
    };

    const logout = useCallback(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
    }, []);

    return token
        ? <PanelView token={token} onLogout={logout} />
        : <LoginView onLogin={login} />;
}

export default AdminOznam;
